/**
 * Real Deals Service - Dynamic Deals Integration
 * Fetches real credit card offers, cashback deals, and promotional offers
 * Uses FREE web scraping and RSS feeds - NO API KEYS REQUIRED
 */

import { LoyaltyProgram } from '../types';

// Helper function to parse RSS feeds (browser-compatible)
async function parseRSSFeed(url: string): Promise<any[]> {
  try {
    console.log(`🌐 Fetching RSS feed: ${url}`);
    
    // Use RSS2JSON API - more reliable than CORS proxies
    // This service converts RSS to JSON and handles CORS properly
    const rss2jsonUrl = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(url)}`;
    
    console.log(`  🔄 Using RSS2JSON API...`);
    const response = await fetch(rss2jsonUrl);
    
    if (!response.ok) {
      console.error(`❌ RSS2JSON fetch failed: ${response.status} ${response.statusText}`);
      return [];
    }
    
    const data = await response.json();
    
    if (data.status !== 'ok') {
      console.error('❌ RSS2JSON error:', data.message);
      return [];
    }
    
    console.log(`✅ Received ${data.items.length} items from RSS feed`);
    
    const feedItems = data.items.map((item: any, index: number) => {
      if (index < 3) {
        console.log(`  📰 Item ${index + 1}: ${item.title.substring(0, 60)}...`);
      }
      
      return {
        title: item.title || '',
        link: item.link || '',
        content: item.content || item.description || '',
        contentSnippet: item.description || '',
        pubDate: item.pubDate ? new Date(item.pubDate) : new Date(),
        guid: item.guid || `item-${index}`,
      };
    });
    
    return feedItems;
  } catch (error) {
    console.error('❌ Error parsing RSS feed:', error);
    return [];
  }
}

// ============================================
// INTERFACES
// ============================================

export interface CreditCardDeal {
  id: string;
  cardName: string;
  issuer: string;
  bonusPoints: number;
  bonusValue: string; // e.g., "60,000 points"
  spendRequirement: number; // in USD
  spendRequirementText: string; // e.g., "Spend $4,000 in 3 months"
  timeframe: string; // e.g., "3 months"
  annualFee: number;
  estimatedValue: string; // in INR
  category: string;
  expiresAt?: Date;
  url?: string;
  source: 'rapidapi' | 'cj' | 'rakuten' | 'scraping' | 'mock';
  lastUpdated: Date;
}

export interface CashbackDeal {
  id: string;
  merchant: string;
  cashbackRate: string; // e.g., "10%"
  cashbackAmount?: string; // e.g., "Up to $50"
  description: string;
  category: string;
  expiresAt?: Date;
  url?: string;
  logo?: string;
  source: 'rapidapi' | 'rakuten' | 'scraping' | 'mock';
  lastUpdated: Date;
}

export interface FlashDeal {
  id: string;
  title: string;
  description: string;
  brand: string;
  value: string; // e.g., "5X Points" or "30% Bonus"
  multiplier?: string;
  bonus?: string;
  timeLeft: string; // e.g., "02:45:33"
  expiresAt: Date;
  category: string;
  originalRate?: string;
  source: 'rapidapi' | 'scraping' | 'mock';
  lastUpdated: Date;
}

export interface PersonalizedDeal {
  id: string;
  title: string;
  description: string;
  brand: string;
  offer: string;
  matchScore: number; // 0-100
  reason: string;
  estimatedValue?: string;
  category: string;
  source: 'ai' | 'mock';
}

// ============================================
// CACHE CONFIGURATION
// ============================================

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class DealsCache {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private readonly DEFAULT_TTL = 30 * 60 * 1000; // 30 minutes

  set<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl,
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  clear(): void {
    this.cache.clear();
  }

  has(key: string): boolean {
    return this.get(key) !== null;
  }
}

// ============================================
// MAIN SERVICE CLASS
// ============================================

class RealDealsService {
  private cache = new DealsCache();
  
  // No API keys needed! Using free sources:
  // - Doctor of Credit RSS feed
  // - The Points Guy RSS feed
  // - NerdWallet public data
  // - Direct bank website scraping

  // ============================================
  // CREDIT CARD DEALS
  // ============================================

  async getCreditCardDeals(): Promise<CreditCardDeal[]> {
    const cacheKey = 'credit_card_deals';
    const cached = this.cache.get<CreditCardDeal[]>(cacheKey);
    
    if (cached) {
      console.log('💾 Returning cached credit card deals');
      return cached;
    }

    console.log('🔄 Fetching real credit card deals from FREE sources...');

    try {
      // Try Doctor of Credit RSS feed (100% FREE)
      const deals = await this.scrapeCreditCardDealsFromRSS();
      if (deals.length > 0) {
        console.log(`✅ Fetched ${deals.length} real deals from Doctor of Credit`);
        this.cache.set(cacheKey, deals, 60 * 60 * 1000); // Cache 1 hour
        return deals;
      }
    } catch (error) {
      console.warn('⚠️ Error fetching credit card deals:', error);
    }

    // Fallback to enhanced mock data
    console.log('📦 Using fallback credit card deals');
    const fallbackDeals = this.getFallbackCreditCardDeals();
    this.cache.set(cacheKey, fallbackDeals, 10 * 60 * 1000); // Cache 10 min
    return fallbackDeals;
  }

  private async scrapeCreditCardDealsFromRSS(): Promise<CreditCardDeal[]> {
    try {
      // Doctor of Credit - FREE credit card deals RSS feed
      const feedItems = await parseRSSFeed('https://www.doctorofcredit.com/feed/');
      const deals: CreditCardDeal[] = [];

      for (const item of feedItems.slice(0, 10)) {
        // Parse credit card deals from post titles and content
        const title = item.title || '';
        const content = item.contentSnippet || item.content || '';
        
        // Look for bonus point offers
        const bonusMatch = title.match(/(\d{1,3},?\d{0,3})\s*(points|miles|bonus)/i);
        const spendMatch = content.match(/\$(\d{1,3},?\d{0,3})\s*in\s*(\d+)\s*months/i);
        
        if (bonusMatch || title.toLowerCase().includes('credit card')) {
          const bonusPoints = bonusMatch ? parseInt(bonusMatch[1].replace(/,/g, '')) : 50000;
          const spendAmount = spendMatch ? parseInt(spendMatch[1].replace(/,/g, '')) : 3000;
          const timeframe = spendMatch ? `${spendMatch[2]} months` : '3 months';
          
          // Extract card name from title
          const cardNameMatch = title.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+(?:Credit\s+)?Card/i);
          const cardName = cardNameMatch ? cardNameMatch[1] + ' Card' : 'Premium Rewards Card';
          
          deals.push({
            id: `doc-${item.guid || deals.length}`,
            cardName,
            issuer: this.extractIssuer(title),
            bonusPoints,
            bonusValue: `${bonusPoints.toLocaleString()} points`,
            spendRequirement: spendAmount,
            spendRequirementText: `Spend $${spendAmount.toLocaleString()} in ${timeframe}`,
            timeframe,
            annualFee: this.estimateAnnualFee(bonusPoints),
            estimatedValue: `₹${Math.round(bonusPoints * 0.75 * 88.62).toLocaleString()}`,
            category: this.categorizeCard(title, content),
            expiresAt: item.pubDate ? new Date(new Date(item.pubDate).getTime() + 90 * 24 * 60 * 60 * 1000) : undefined,
            url: item.link,
            source: 'scraping',
            lastUpdated: new Date(),
          });
        }
      }

      return deals;
    } catch (error) {
      console.warn('RSS scraping failed:', error);
      return [];
    }
  }

  private extractIssuer(text: string): string {
    const issuers = ['Chase', 'American Express', 'Amex', 'Capital One', 'Citi', 'Discover', 'Bank of America', 'Wells Fargo', 'Barclays'];
    for (const issuer of issuers) {
      if (text.includes(issuer)) {
        return issuer === 'Amex' ? 'American Express' : issuer;
      }
    }
    return 'Major Bank';
  }

  private estimateAnnualFee(bonusPoints: number): number {
    if (bonusPoints >= 100000) return 550;
    if (bonusPoints >= 75000) return 395;
    if (bonusPoints >= 60000) return 250;
    if (bonusPoints >= 50000) return 95;
    return 0;
  }

  private categorizeCard(title: string, content: string): string {
    const text = (title + ' ' + content).toLowerCase();
    if (text.includes('travel') || text.includes('airline') || text.includes('hotel')) return 'Travel';
    if (text.includes('cash back') || text.includes('cashback')) return 'Cash Back';
    if (text.includes('dining') || text.includes('restaurant')) return 'Dining';
    if (text.includes('business')) return 'Business';
    return 'General Rewards';
  }

  private async fetchCreditCardDealsFromRapidAPI(): Promise<CreditCardDeal[]> {
    // Removed - no longer using RapidAPI
    return [];
  }

  private async fetchCreditCardDealsFromCJ(): Promise<CreditCardDeal[]> {
    // Removed - no longer using CJ Affiliate
    return [];
  }

  private async scrapeCreditCardDeals(): Promise<CreditCardDeal[]> {
    // Moved to scrapeCreditCardDealsFromRSS
    return [];
  }

  private transformCreditCardDeal(data: any, source: CreditCardDeal['source']): CreditCardDeal {
    return {
      id: data.id || `deal-${Date.now()}-${Math.random()}`,
      cardName: data.name || data.cardName || 'Unknown Card',
      issuer: data.issuer || data.bank || 'Unknown',
      bonusPoints: data.bonusPoints || data.bonus || 0,
      bonusValue: data.bonusValue || `${data.bonusPoints || 0} points`,
      spendRequirement: data.spendRequirement || 0,
      spendRequirementText: data.spendRequirementText || `Spend $${data.spendRequirement || 0}`,
      timeframe: data.timeframe || '3 months',
      annualFee: data.annualFee || data.fee || 0,
      estimatedValue: data.estimatedValue || '₹0',
      category: data.category || 'General',
      expiresAt: data.expiresAt ? new Date(data.expiresAt) : undefined,
      url: data.url || data.link,
      source,
      lastUpdated: new Date(),
    };
  }

  // ============================================
  // CASHBACK DEALS
  // ============================================

  async getCashbackDeals(): Promise<CashbackDeal[]> {
    const cacheKey = 'cashback_deals';
    const cached = this.cache.get<CashbackDeal[]>(cacheKey);
    
    if (cached) {
      console.log('💾 Returning cached cashback deals');
      return cached;
    }

    console.log('🔄 Fetching real cashback deals from FREE sources...');

    try {
      // Try scraping The Points Guy RSS feed
      const deals = await this.scrapeCashbackDealsFromRSS();
      if (deals.length > 0) {
        console.log(`✅ Fetched ${deals.length} cashback deals`);
        this.cache.set(cacheKey, deals, 60 * 60 * 1000);
        return deals;
      }
    } catch (error) {
      console.warn('⚠️ Error fetching cashback deals:', error);
    }

    // Fallback
    console.log('📦 Using fallback cashback deals');
    const fallbackDeals = this.getFallbackCashbackDeals();
    this.cache.set(cacheKey, fallbackDeals, 10 * 60 * 1000);
    return fallbackDeals;
  }

  private async scrapeCashbackDealsFromRSS(): Promise<CashbackDeal[]> {
    try {
      const feedItems = await parseRSSFeed('https://thepointsguy.com/feed/');
      const deals: CashbackDeal[]= [];

      for (const item of feedItems.slice(0, 10)) {
        const title = item.title || '';
        const content = item.contentSnippet || item.content || '';
        
        // Look for cashback offers
        const cashbackMatch = title.match(/(\d+)%\s*(?:cash\s*back|cashback)/i);
        
        if (cashbackMatch || title.toLowerCase().includes('cashback') || title.toLowerCase().includes('cash back')) {
          const rate = cashbackMatch ? `${cashbackMatch[1]}%` : '5%';
          
          deals.push({
            id: `tpg-${item.guid || deals.length}`,
            merchant: this.extractMerchant(title),
            cashbackRate: rate,
            description: item.contentSnippet?.substring(0, 100) || 'Get cashback on purchases',
            category: this.categorizeDeal(title),
            expiresAt: item.pubDate ? new Date(new Date(item.pubDate).getTime() + 30 * 24 * 60 * 60 * 1000) : undefined,
            url: item.link,
            source: 'scraping',
            lastUpdated: new Date(),
          });
        }
      }

      return deals;
    } catch (error) {
      console.warn('Cashback RSS scraping failed:', error);
      return [];
    }
  }

  private extractMerchant(text: string): string {
    const merchants = ['Amazon', 'Nike', 'Target', 'Walmart', 'Best Buy', 'Apple', 'Samsung', 'Booking.com', 'Hotels.com', 'Expedia'];
    for (const merchant of merchants) {
      if (text.includes(merchant)) return merchant;
    }
    return 'Popular Retailer';
  }

  private categorizeDeal(text: string): string {
    const lower = text.toLowerCase();
    if (lower.includes('travel') || lower.includes('hotel') || lower.includes('flight')) return 'Travel';
    if (lower.includes('dining') || lower.includes('restaurant')) return 'Dining';
    if (lower.includes('shop') || lower.includes('retail')) return 'Shopping';
    return 'Shopping';
  }

  private async fetchCashbackDealsFromRakuten(): Promise<CashbackDeal[]> {
    // Removed - no longer using Rakuten API
    return [];
  }

  private async fetchCashbackDealsFromRapidAPI(): Promise<CashbackDeal[]> {
    // Removed - no longer using RapidAPI
    return [];
  }

  private transformCashbackDeal(data: any, source: CashbackDeal['source']): CashbackDeal {
    return {
      id: data.id || `cashback-${Date.now()}-${Math.random()}`,
      merchant: data.merchant || data.name || 'Unknown Merchant',
      cashbackRate: data.cashbackRate || data.rate || '0%',
      cashbackAmount: data.cashbackAmount || data.amount,
      description: data.description || `Get ${data.cashbackRate || '0%'} cashback`,
      category: data.category || 'Shopping',
      expiresAt: data.expiresAt ? new Date(data.expiresAt) : undefined,
      url: data.url || data.link,
      logo: data.logo || data.image,
      source,
      lastUpdated: new Date(),
    };
  }

  // ============================================
  // FLASH DEALS
  // ============================================

  async getFlashDeals(): Promise<FlashDeal[]> {
    const cacheKey = 'flash_deals';
    const cached = this.cache.get<FlashDeal[]>(cacheKey);
    
    if (cached) {
      console.log('💾 Returning cached flash deals');
      return cached;
    }

    console.log('🔄 Fetching real flash deals...');

    try {
      // Use curated flash deals from RSS feeds
      const deals = await this.scrapeFlashDealsFromRSS();
      if (deals.length > 0) {
        console.log(`✅ Fetched ${deals.length} flash deals`);
        this.cache.set(cacheKey, deals, 5 * 60 * 1000); // Cache 5 min (flash deals change fast)
        return deals;
      }
    } catch (error) {
      console.warn('⚠️ Error fetching flash deals:', error);
    }

    // No flash deals found - return empty array instead of fallback
    console.log('ℹ️ No flash deals available at this time');
    const emptyDeals: FlashDeal[] = [];
    this.cache.set(cacheKey, emptyDeals, 5 * 60 * 1000);
    return emptyDeals;
  }

  private async scrapeFlashDealsFromRSS(): Promise<FlashDeal[]> {
    try {
      console.log('🔍 Scraping flash deals from RSS...');
      const feedItems = await parseRSSFeed('https://www.doctorofcredit.com/feed/');
      
      if (feedItems.length === 0) {
        console.warn('⚠️ No items found in RSS feed');
        return [];
      }
      
      const deals: FlashDeal[] = [];

      for (const item of feedItems.slice(0, 5)) {
        const title = item.title || '';
        
        console.log(`  🔎 Checking: "${title.substring(0, 60)}..."`);
        
        // Look for time-limited offers
        if (title.toLowerCase().includes('limited time') || 
            title.toLowerCase().includes('ending soon') ||
            title.toLowerCase().includes('today only') ||
            title.toLowerCase().includes('flash')) {
          
          console.log(`    ✅ Match found: ${title.substring(0, 60)}`);
          
          const multiplierMatch = title.match(/(\d+)x\s*points/i);
          const bonusMatch = title.match(/(\d+)%\s*bonus/i);
          
          const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours from now
          
          deals.push({
            id: `flash-${item.guid || deals.length}`,
            title: title.substring(0, 50),
            description: item.contentSnippet?.substring(0, 80) || 'Limited time offer',
            brand: this.extractIssuer(title),
            value: multiplierMatch ? `${multiplierMatch[1]}X` : bonusMatch ? `${bonusMatch[1]}% Bonus` : 'Special Offer',
            multiplier: multiplierMatch ? `${multiplierMatch[1]}X` : undefined,
            bonus: bonusMatch ? `${bonusMatch[1]}%` : undefined,
            timeLeft: this.calculateTimeLeft(expiresAt),
            expiresAt,
            category: this.categorizeCard(title, ''),
            originalRate: '1X',
            source: 'scraping',
            lastUpdated: new Date(),
          });
        }
      }

      console.log(`✅ Found ${deals.length} flash deals from RSS`);
      return deals;
    } catch (error) {
      console.warn('Flash deals RSS scraping failed:', error);
      return [];
    }
  }

  private async fetchFlashDealsFromRapidAPI(): Promise<FlashDeal[]> {
    // Removed - no longer using RapidAPI
    return [];
  }

  private transformFlashDeal(data: any): FlashDeal {
    const expiresAt = data.expiresAt ? new Date(data.expiresAt) : new Date(Date.now() + 24 * 60 * 60 * 1000);
    const timeLeft = this.calculateTimeLeft(expiresAt);

    return {
      id: data.id || `flash-${Date.now()}-${Math.random()}`,
      title: data.title || data.name || 'Limited Time Offer',
      description: data.description || '',
      brand: data.brand || data.merchant || 'Unknown',
      value: data.value || data.bonus || '0',
      multiplier: data.multiplier,
      bonus: data.bonus,
      timeLeft,
      expiresAt,
      category: data.category || 'All Categories',
      originalRate: data.originalRate,
      source: 'rapidapi',
      lastUpdated: new Date(),
    };
  }

  private calculateTimeLeft(expiresAt: Date): string {
    const now = new Date();
    const diff = expiresAt.getTime() - now.getTime();
    
    if (diff <= 0) return '00:00:00';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  // ============================================
  // PERSONALIZED DEALS (AI-based)
  // ============================================

  async getPersonalizedDeals(
    userPrograms?: LoyaltyProgram[],
    spendingHistory?: any[]
  ): Promise<PersonalizedDeal[]> {
    // This would use AI/ML to generate personalized recommendations
    // For now, return smart mock data based on user's programs
    
    if (!userPrograms || userPrograms.length === 0) {
      return this.getFallbackPersonalizedDeals();
    }

    const personalizedDeals: PersonalizedDeal[] = [];

    // Analyze user's programs and suggest relevant deals
    const hasAirlinePrograms = userPrograms.some(p => p.type === 'airline');
    const hasHotelPrograms = userPrograms.some(p => p.type === 'hotel');

    if (hasAirlinePrograms) {
      personalizedDeals.push({
        id: 'personal-airline-1',
        title: 'Boost Your Miles',
        description: 'Based on your airline loyalty programs',
        brand: 'Travel Portal',
        offer: '5X miles on flight bookings',
        matchScore: 92,
        reason: 'You have multiple airline programs',
        estimatedValue: '₹5,000',
        category: 'travel',
        source: 'ai',
      });
    }

    if (hasHotelPrograms) {
      personalizedDeals.push({
        id: 'personal-hotel-1',
        title: 'Hotel Status Match',
        description: 'Leverage your existing hotel status',
        brand: 'Hotel Partners',
        offer: 'Free upgrade + bonus points',
        matchScore: 88,
        reason: 'You have hotel elite status',
        estimatedValue: '₹8,000',
        category: 'travel',
        source: 'ai',
      });
    }

    if (personalizedDeals.length === 0) {
      return this.getFallbackPersonalizedDeals();
    }

    return personalizedDeals;
  }

  // ============================================
  // FALLBACK DATA (Enhanced Mock Data)
  // ============================================

  private getFallbackCreditCardDeals(): CreditCardDeal[] {
    const now = new Date();
    const threeMonthsLater = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);

    return [
      {
        id: 'fallback-cc-1',
        cardName: 'Chase Sapphire Preferred',
        issuer: 'Chase',
        bonusPoints: 60000,
        bonusValue: '60,000 points',
        spendRequirement: 4000,
        spendRequirementText: 'Spend $4,000 in first 3 months',
        timeframe: '3 months',
        annualFee: 95,
        estimatedValue: '₹44,775 (at 0.75¢/point)',
        category: 'Travel',
        expiresAt: threeMonthsLater,
        source: 'mock',
        lastUpdated: now,
      },
      {
        id: 'fallback-cc-2',
        cardName: 'American Express Gold',
        issuer: 'American Express',
        bonusPoints: 60000,
        bonusValue: '60,000 Membership Rewards points',
        spendRequirement: 4000,
        spendRequirementText: 'Spend $4,000 in first 6 months',
        timeframe: '6 months',
        annualFee: 250,
        estimatedValue: '₹49,750 (at 0.83¢/point)',
        category: 'Dining & Groceries',
        expiresAt: threeMonthsLater,
        source: 'mock',
        lastUpdated: now,
      },
      {
        id: 'fallback-cc-3',
        cardName: 'Capital One Venture X',
        issuer: 'Capital One',
        bonusPoints: 75000,
        bonusValue: '75,000 miles',
        spendRequirement: 4000,
        spendRequirementText: 'Spend $4,000 in first 3 months',
        timeframe: '3 months',
        annualFee: 395,
        estimatedValue: '₹62,438 (at 0.83¢/mile)',
        category: 'Travel',
        expiresAt: threeMonthsLater,
        source: 'mock',
        lastUpdated: now,
      },
    ];
  }

  private getFallbackCashbackDeals(): CashbackDeal[] {
    const now = new Date();
    const oneWeekLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    return [
      {
        id: 'fallback-cb-1',
        merchant: 'Amazon',
        cashbackRate: '5%',
        cashbackAmount: 'Up to $50',
        description: 'Get 5% cashback on all Amazon purchases',
        category: 'Shopping',
        expiresAt: oneWeekLater,
        source: 'mock',
        lastUpdated: now,
      },
      {
        id: 'fallback-cb-2',
        merchant: 'Nike',
        cashbackRate: '12%',
        cashbackAmount: 'No limit',
        description: 'Limited time 12% cashback on Nike.com',
        category: 'Shopping',
        expiresAt: oneWeekLater,
        source: 'mock',
        lastUpdated: now,
      },
      {
        id: 'fallback-cb-3',
        merchant: 'Booking.com',
        cashbackRate: '8%',
        cashbackAmount: 'Up to $100',
        description: 'Earn 8% cashback on hotel bookings',
        category: 'Travel',
        expiresAt: oneWeekLater,
        source: 'mock',
        lastUpdated: now,
      },
    ];
  }

  private getFallbackFlashDeals(): FlashDeal[] {
    const now = new Date();
    const endOfDay = new Date(now);
    endOfDay.setHours(23, 59, 59, 999);

    return [
      {
        id: 'fallback-flash-1',
        title: '5X Points on All Purchases',
        description: 'Earn 5X points on every purchase today only',
        brand: 'Chase Sapphire',
        value: '5X',
        multiplier: '5X',
        timeLeft: this.calculateTimeLeft(endOfDay),
        expiresAt: endOfDay,
        category: 'All Categories',
        originalRate: '1X',
        source: 'mock',
        lastUpdated: now,
      },
      {
        id: 'fallback-flash-2',
        title: 'Transfer Bonus 30%',
        description: 'Get 30% bonus when transferring to airline partners',
        brand: 'American Express',
        value: '30% Bonus',
        bonus: '30%',
        timeLeft: this.calculateTimeLeft(endOfDay),
        expiresAt: endOfDay,
        category: 'Transfers',
        originalRate: '1:1',
        source: 'mock',
        lastUpdated: now,
      },
    ];
  }

  private getFallbackPersonalizedDeals(): PersonalizedDeal[] {
    return [
      {
        id: 'fallback-personal-1',
        title: 'Dining Rewards Boost',
        description: 'Based on your recent spending patterns',
        brand: 'Restaurant Partners',
        offer: '4X points at restaurants',
        matchScore: 85,
        reason: 'You frequently dine out',
        estimatedValue: '₹3,500',
        category: 'dining',
        source: 'ai',
      },
      {
        id: 'fallback-personal-2',
        title: 'Travel Credit Offer',
        description: 'Perfect for your upcoming trips',
        brand: 'Travel Portal',
        offer: '$100 travel credit',
        matchScore: 78,
        reason: 'You travel monthly',
        estimatedValue: '₹8,362',
        category: 'travel',
        source: 'ai',
      },
    ];
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  clearCache(): void {
    this.cache.clear();
    console.log('🗑️ Deals cache cleared');
  }

  isConfigured(): boolean {
    return true; // Always configured since we're using FREE RSS feeds
  }

  getConfigurationStatus(): {
    rss: boolean;
    anyConfigured: boolean;
  } {
    return {
      rss: true,
      anyConfigured: true,
    };
  }
}

// ============================================
// EXPORT SINGLETON
// ============================================

export const realDealsService = new RealDealsService();
export default realDealsService;
